import { EstadoTransitoInventarioENUM, ROUTER_PATHS, sanitizeTodos, useCheckPermissions, useTableFilter, useTableServerSideFiltering } from "@/shared";
import { useFetchGetTransitoInventario, useFetchPutTransitoInventario } from "@/shared/actions/erp/transito_inventaro.actions";
import { GeneralTable, LoaderComponent, TableTabsScene, } from "@/shared/components/ui";
import { useTableSolicitudTransferenciaInventario } from "@/shared/hooks/useTableSolicitudTransferenciaInventario";
import { ITransitoInventario } from "@/shared/interfaces/erp/administration/transito_inventario.interface";
import { useNavigate } from "react-router-dom";
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import { useFetchCreateTransferenciaInventario } from "@/shared/actions/erp";
import { useEffect, useState } from "react";
import { useGetConfiguracionTransito } from "@/erp/inventario/configuracion-transito/actions/configuracion-inventario.actions";
import { toast } from "react-toastify";
import { useUiStore } from "@/store/ui";
import { PermissionsEnum } from "@/auth/interfaces";
import { Grid } from "@mui/material";
import { TransferenciaInventarioFiltro } from "@/erp/inventario/transferencia-inventario/shared/transferencia-inventario/TransferenciaInventarioFiltro";
import { useForm } from "react-hook-form";
import { TransferenciaInventarioFilter } from "@/erp/inventario/transferencia-inventario";
import dayjs from "dayjs";

interface TableRecepcionTransferenciaInventarioProps {
  estado_transito?: string
  enable_action?: boolean
  regulacion?: boolean
}

const TableRecepcionTransitoInventario = ({
  estado_transito = EstadoTransitoInventarioENUM.EN_TRANSITO,
  enable_action = true,
  regulacion = false
}: TableRecepcionTransferenciaInventarioProps) => {

  if (regulacion) {
    useCheckPermissions(PermissionsEnum.habilitar_regulacion_transito)
  }

  const navigate = useNavigate();

  const setConfirmDialogIsOpen = useUiStore(s => s.setConfirmDialogIsOpen)
  const setConfirmDialog = useUiStore(s => s.setConfirmDialog)

  // para la transferencia de productos
  const { mutateAsync: mutateAsyncTransferenciaProducto, isPending: isPendingTrasladoInventario
  } = useFetchCreateTransferenciaInventario();

  const { columnaRecepcionTrasladoInventario } = useTableSolicitudTransferenciaInventario();

  /// form
  const form = useForm<TransferenciaInventarioFilter>({
    defaultValues: {
      producto_codigo: null,
      producto_serie: null,
      fecha_creacion_after: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
      fecha_creacion_before: dayjs().format('YYYY-MM-DD')
    }
  });

  const {
    globalFilter,
    pagination,
    setPagination,
  } = useTableFilter();

  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  const {
    pageIndex,
    pageSize
  } = pagination;

  const {
    isLoading, data: transferenciaProducto,
    isRefetching,
  } = useFetchGetTransitoInventario(
    {
      page: pageIndex + 1,
      page_size: pageSize,
      estado_transito: estado_transito,
      numero_referencia: globalFilter,
      producto_codigo: sanitizeTodos(form.getValues().producto_codigo),
      producto_serie: sanitizeTodos(form.getValues().producto_serie),
      fecha_creacion_after: form.getValues().fecha_creacion_after,
      fecha_creacion_before: form.getValues().fecha_creacion_before,
      ...filterObject,
      filterByState: false
    }
  );

  const [listaConfiguracionTransito, setListaConfiguracionTransito] = useState<any[]>([]);
  const useGetConfiguracionTransitoMutation = useGetConfiguracionTransito();
  const { mutateAsync: mutateAsyncPutTransito } = useFetchPutTransitoInventario({})

  useEffect(() => {
    const fetchData = async () => {
      const response = await useGetConfiguracionTransitoMutation.mutateAsync([]) as any;
      setListaConfiguracionTransito(response.data?.data ?? []);
    };
    fetchData();
  }, []);

  const onEdit = (data: ITransitoInventario) => {
    navigate(`${ROUTER_PATHS.inventario.recepcionEditTransferenciaInventarioNav}/${data.id_transito_inventario}`);
  }

  const onRegulacion = async (data: ITransitoInventario) => {

    setConfirmDialogIsOpen(true);

    setConfirmDialog({
      isOpen: true,
      title: "Habilitar Transito ?",
      subtitle:
        "Estás a punto de habilitar un transito vencido. Una vez realizado, el cambio será irreversible. ¿Estás seguro de que deseas continuar?",
      onClose() {
        setConfirmDialogIsOpen(false);
      },
      onConfirm: async () => {
        setConfirmDialogIsOpen(false);
        // salen de devueltos
        let codigo_bodega_origen = listaConfiguracionTransito[0]?.bodega_custodia || '';
        let codigo_ubicacion_origen = listaConfiguracionTransito[0]?.ubicacion_custodia || '';
        // regresan a transito para seguir el proceso
        let codigo_bodega_destino = data?.bodega_transito_data?.codigo;
        let codigo_ubicacion_destino = data?.ubicacion_transito_data?.codigo;

        if (codigo_bodega_origen == '') {
          toast('No existe la configuracion de transitos. Bodega Custodia no existe');
          return;
        }

        if (codigo_ubicacion_origen == '') {
          toast('No existe la configuracion de transitos. Ubicacion Custodia no existe');
          return;
        }

        const productosEnviar = [] as any;
        data.productos.forEach(item => {
          if (item.usa_metros && item.contiene_serie) {
            productosEnviar.push({
              codigo: item.codigo,
              cantidad: item.series?.length + "" || "0",
              series: item.series,
              cantidad_metros: item.cantidad_metros,
              series_metros: item.series_metros
            });
          }
          else if (!item.usa_metros && item.contiene_serie) {
            productosEnviar.push({
              codigo: item.codigo,
              cantidad: item.series?.length + "" || "0",
              series: item.series,
            });
          }
          else {
            productosEnviar.push({
              codigo: item.codigo,
              cantidad: item.cantidad_esperada + "",
              series: []
            });
          }
        });

        const payloadEquipment: any = {
          bodega_origen: codigo_bodega_origen,
          ubicacion_origen: codigo_ubicacion_origen,
          bodega_destino: codigo_bodega_destino,
          ubicacion_destino: codigo_ubicacion_destino,
          productos: productosEnviar,
        };

        await mutateAsyncTransferenciaProducto(payloadEquipment, {
          onSuccess: async () => {
            toast.success("Transito habilitado");
            // cambiar de estado el transito
            await mutateAsyncPutTransito({
              id: data?.id_transito_inventario!,
              data: {
                estado_transito: EstadoTransitoInventarioENUM.EN_TRANSITO
              }
            });
            // window.location.reload()
          },
          onError: () => {
            toast.error('Error al retornar el equipamiento a transito');
          }
        });
      }
    })
  }

  // spinner hasta cargar de la transferencia
  if (isPendingTrasladoInventario) {
    return <LoaderComponent />
  }

  return (
    <Grid container spacing={2} sx={{ paddingTop: 2 }}>

      <Grid item xs={12}>

        <TransferenciaInventarioFiltro form={form} />

        <GeneralTable<ITransitoInventario>
          columns={columnaRecepcionTrasladoInventario}
          data={transferenciaProducto?.data.data || []}
          //
          isLoading={isLoading}
          isRefetching={isRefetching}
          // // search
          enableGlobalFilter={false}
          // onGlobalFilterChange={setGlobalFilter}
          // // pagination
          pagination={pagination}
          onPaging={setPagination}
          rowCount={transferenciaProducto?.data.count}
          // // actions
          actionsColumnSize={120}
          // crud
          onEdit={regulacion ? onRegulacion : onEdit}
          canDelete={false}
          positionActionsColumn='first'
          enableColumnActions={enable_action}
          enableEditing={enable_action}
          editIcon={regulacion ? <SendIcon /> : <EditIcon />}

          ///* filters - server side
          enableManualFiltering={true}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          enableSorting={false}

        />
      </Grid>
    </Grid>
  );
}


export default TableRecepcionTransitoInventario;
